import React, { useState } from 'react';
import { Table, Tag, Typography, Select, Space, Input } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useAuditLogs } from '@/hooks/useAudit';
import type { AuditLog } from '@/api/audit';
import ExportButtons from '@/components/common/ExportButtons';

const { Title } = Typography;

const AuditList: React.FC = () => {
  const [search, setSearch] = useState('');
  const [actionFilter, setActionFilter] = useState<string>('');
  const [resourceFilter, setResourceFilter] = useState<string>('');
  const [page, setPage] = useState(1);
  
  const { data, isLoading } = useAuditLogs({ 
    search, 
    page, 
    action: actionFilter, 
    resource_type: resourceFilter 
  });

  const exportColumns = [
    { header: 'Usuário', key: 'user_name' },
    { header: 'Ação', key: 'action' },
    { header: 'Recurso', key: 'resource_type' },
    { header: 'Descrição', key: 'description' },
    { header: 'IP', key: 'ip_address' },
    { header: 'Data/Hora', key: 'created_at' },
  ];

  const columns: ColumnsType<AuditLog> = [
    {
      title: 'Usuário',
      dataIndex: 'user_name',
      key: 'user_name',
    },
    {
      title: 'Ação',
      dataIndex: 'action',
      key: 'action',
      render: (action: string) => (
        <Tag color={
          action === 'CREATE' ? 'green' :
          action === 'UPDATE' ? 'blue' :
          action === 'DELETE' ? 'red' :
          action === 'LOGIN' ? 'purple' :
          action === 'LOGOUT' ? 'orange' : 'default'
        }>
          {action === 'CREATE' ? 'Criação' :
           action === 'UPDATE' ? 'Atualização' :
           action === 'DELETE' ? 'Exclusão' :
           action === 'LOGIN' ? 'Login' :
           action === 'LOGOUT' ? 'Logout' : action}
        </Tag>
      ),
    },
    {
      title: 'Recurso',
      dataIndex: 'resource_type',
      key: 'resource_type',
      render: (type: string) => (
        <Tag>
          {type === 'USER' ? 'Usuário' :
           type === 'COMPANY' ? 'Empresa' :
           type === 'DEVICE' ? 'Dispositivo' :
           type === 'SESSION' ? 'Sessão' : 'Sistema'}
        </Tag>
      ),
    },
    {
      title: 'Descrição',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
    },
    {
      title: 'IP',
      dataIndex: 'ip_address',
      key: 'ip_address',
    },
    {
      title: 'Data/Hora',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date: string) => new Date(date).toLocaleString('pt-BR'),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <Title level={4}>Auditoria</Title>
        <Space>
          <Input
            placeholder="Buscar..."
            prefix={<SearchOutlined />}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ width: 200 }}
            allowClear
          />
          <Select
            placeholder="Ação"
            value={actionFilter}
            onChange={setActionFilter}
            style={{ width: 130 }}
            allowClear
          >
            <Select.Option value="">Todas</Select.Option>
            <Select.Option value="CREATE">Criação</Select.Option>
            <Select.Option value="UPDATE">Atualização</Select.Option>
            <Select.Option value="DELETE">Exclusão</Select.Option>
            <Select.Option value="LOGIN">Login</Select.Option>
            <Select.Option value="LOGOUT">Logout</Select.Option>
          </Select>
          <Select
            placeholder="Recurso"
            value={resourceFilter}
            onChange={setResourceFilter}
            style={{ width: 130 }}
            allowClear
          >
            <Select.Option value="">Todos</Select.Option>
            <Select.Option value="USER">Usuário</Select.Option>
            <Select.Option value="COMPANY">Empresa</Select.Option>
            <Select.Option value="DEVICE">Dispositivo</Select.Option>
            <Select.Option value="SESSION">Sessão</Select.Option>
          </Select>
          <ExportButtons
            title="Relatório de Auditoria"
            columns={exportColumns}
            data={data?.results || []}
            filename="auditoria"
          />
        </Space>
      </div>

      <Table
        columns={columns}
        dataSource={data?.results || []}
        loading={isLoading}
        rowKey="id"
        pagination={{
          current: page,
          total: data?.count || 0,
          pageSize: 20,
          onChange: setPage,
          showTotal: (total) => `Total: ${total} registros`,
        }}
      />
    </div>
  );
};

export default AuditList;
